/**
 * Storage factory — picks the right adapter based on the runtime env.
 *
 * Web → ``WebStorage`` (``localStorage`` + JSON wrap).
 * Tauri → ``TauriStorage`` (``@tauri-apps/plugin-store``).
 * Secrets (Tauri) → ``KeyringStorage`` (OS keychain via Rust ``keyring``).
 *
 * The factory is memoised — calling ``getStorage()`` twice returns the
 * same singleton so Zustand persist middleware doesn't re-init across
 * hot-reloads.
 */

import { getEnv } from "../env.js";
import { KeyringStorage } from "./keyring.js";
import { TauriStorage } from "./tauri.js";
import type { AndoStorage } from "./types.js";
import { WebStorage } from "./web.js";

let cached: AndoStorage | null = null;
let cachedSecrets: AndoStorage | null = null;

export function getStorage(): AndoStorage {
  if (cached) return cached;
  cached = getEnv().isTauri ? new TauriStorage() : new WebStorage();
  return cached;
}

export function getSecretStorage(): AndoStorage {
  if (cachedSecrets) return cachedSecrets;
  cachedSecrets = getEnv().isTauri ? new KeyringStorage() : new WebStorage();
  return cachedSecrets;
}

/** Test hook — reset the memoised adapters. */
export function __resetStorageForTests(): void {
  cached = null;
  cachedSecrets = null;
}

export { KeyringStorage } from "./keyring.js";
export { TauriStorage } from "./tauri.js";
export { WebStorage } from "./web.js";
export type { AndoStorage } from "./types.js";
export type { AndoStorage as Storage } from "./types.js";
